<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUpdate, onMounted, onUnmounted, useId, computed } from 'vue'
import { size as sizeConst } from '@/services/application/constantes'
import { bgColors } from '@/services/application/constantes';

type IItem = {
  id: string | number | boolean
  display: string
  [key: string]: any
  bgColor?: keyof typeof bgColors;
}

const props = withDefaults(defineProps<{
  listItems: Array<IItem>
  direction?: 'row' | 'col'
  label?: string
  required?: boolean
  size?: keyof typeof sizeConst
  dataCy?: string,
  useId?: boolean
  disabled?: boolean
  /** round: pill container (default). square: compact container with square corners, as in the dense forms */
  shape?: 'round' | 'square'
}>(), {
  direction: 'row',
  required: false,
  size: 'small',
  useId: false,
  disabled: false,
  shape: 'round',
})

const emit = defineEmits<{
  (e: 'itemSelected', itemSelected: IItem | string | boolean | number): void
}>()

const option = defineModel<IItem | string | boolean | number>()

const containerRef = ref<HTMLElement | null>(null)
const magneticBgRef = ref<HTMLElement | null>(null)
const itemRefs = ref<(HTMLElement | null)[]>([])

const magneticBgStyle = ref({
  opacity: 0,
  left: '0px',
  top: '0px',
  width: '0px',
  height: '0px',
  transition: 'none'
})

onBeforeUpdate(() => {
  itemRefs.value = []
})

const setItemRef = (el: any, index: number) => {
  if (el) {
    itemRefs.value[index] = el as HTMLElement;
  }
}

let isInitialLoad = true;

const idToCompare = computed(() => {
  return props.useId ? option.value : option.value?.id;
});

const updateMagneticBackground = async () => {
  if ((idToCompare.value === null || idToCompare.value === undefined) || !magneticBgRef.value || !containerRef.value) {
    magneticBgStyle.value.opacity = 0;
    magneticBgStyle.value.width = '0px';
    magneticBgStyle.value.transition = 'opacity 0.2s ease-out';
    return;
  }

  await nextTick();

  const selectedIndex = props.listItems.findIndex(item => item.id === idToCompare.value);
  if (selectedIndex === -1 || !itemRefs.value[selectedIndex]) {
    magneticBgStyle.value.opacity = 0;
    magneticBgStyle.value.width = '0px';
    magneticBgStyle.value.transition = 'opacity 0.2s ease-out';
    return;
  }

  const selectedElement = itemRefs.value[selectedIndex] as HTMLElement;
  const offsetLeft = selectedElement.offsetLeft;
  const offsetTop = selectedElement.offsetTop;
  const width = selectedElement.offsetWidth;
  const height = selectedElement.offsetHeight;

  const transition = isInitialLoad
    ? 'none'
    : `all 0.3s cubic-bezier(0.4, 0, 0.2, 1)`;

  magneticBgStyle.value = {
    opacity: 1,
    left: `${offsetLeft}px`,
    top: `${offsetTop}px`,
    width: `${width}px`,
    height: `${height}px`,
    transition: transition
  };

  if (isInitialLoad) {
    requestAnimationFrame(() => {
      isInitialLoad = false;
      if (magneticBgStyle.value.opacity === 1) {
        magneticBgStyle.value.transition = `all 0.3s cubic-bezier(0.4, 0, 0.2, 1)`;
      }
    });
  }
};

watch(option, updateMagneticBackground, { immediate: true, flush: 'post' });

onMounted(() => {
  setTimeout(updateMagneticBackground, 50);

  if (containerRef.value) {
    const resizeObserver = new ResizeObserver(() => {
      updateMagneticBackground();
    });
    resizeObserver.observe(containerRef.value);
    // Consider adding onUnmounted hook to disconnect the observer
    onUnmounted(() => {
      resizeObserver.disconnect();
    });
  }
});

const selectItem = (item: IItem) => {
  if (props.disabled) return;
  if(props.useId) {
    option.value = item.id
    emit('itemSelected', item.id);
    return;
  }
  option.value = item;
  emit('itemSelected', item);
}

</script>

<template>
  <div class="relative max-w-full" :class="{'mt-5': label}">
    <p v-if="label" class="absolute -top-3 left-0 subtitle-field">
      {{ label }} <span v-if="required" class="text-danger-400 text-sm">*</span>
    </p>
    <div ref="containerRef" class="magnetic-container flex items-start relative max-w-full overflow-x-auto mini-scroll"
      :class="[
        sizeConst[size],
        { 'is-square': shape === 'square' },
        `options-${size}`,
        direction === 'col' ? 'flex-col items-stretch w-full' : 'flex-row w-min',
        { 'opacity-70 cursor-not-allowed': disabled },
      ]" role="radiogroup" :aria-label="label || 'Options'" :aria-disabled="disabled">
      <span ref="magneticBgRef" class="magnetic-background" :class="bgColors[ option?.bgColor || 'primary']" aria-hidden="true" :style="magneticBgStyle"></span>

      <button v-for="(listItem, index) in listItems" :key="listItem.id" :ref="(el) => setItemRef(el, index)" type="button"
        role="radio" :aria-checked="idToCompare === listItem.id" class="item-label shrink-0"
        :class="{ 'selected': idToCompare === listItem.id, 'pointer-events-none': disabled }" :disabled="disabled" @click="selectItem(listItem)"
        :data-cy="(dataCy ? dataCy : label ? label : 'option') + '-' + listItem.id">
        <slot name="item"  :item="listItem">
          <slot :item="listItem">
            {{ listItem.display }}
          </slot>
        </slot>
      </button>
    </div>
  </div>
</template>

<style scoped>
@reference "../../assets/css/theme.css";

.magnetic-container {
  background-color: var(--color-fade, #f0f0f0);
  @apply dark:bg-gray-800;
  @apply rounded-2xl;
  border: 1px solid var(--color-base, #cccccc);
  @apply dark:border-gray-700;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.07);
  @apply dark:shadow-none;
  padding: 0.25rem;
  gap: 0.5rem;
  max-width: 100%;
  user-select: none;
}

.magnetic-background {
  position: absolute;
  z-index: 0;
  @apply rounded-xl;
  pointer-events: none;
}

.item-label {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 2rem;
  padding: 0.25rem 0.5rem;
  @apply rounded-xl;
  border: none;
  /* Explicit border reset */
  background: none;
  /* Explicit background reset */
  box-shadow: none;
  /* Explicit shadow reset */
  cursor: pointer;
  @apply text-gray-700 dark:text-gray-300;
  transition: color 0.3s ease;
  white-space: nowrap;
  height: 100%;
  appearance: none;
  -webkit-appearance: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  /* Ensure line-height doesn't cause issues */
  text-decoration: none;
  /* Remove potential underlines */
}

/* Force remove pseudo-elements often used for arrows/indicators */
.item-label::before,
.item-label::after {
  content: none !important;
  /* Use !important as a strong measure if needed */
  display: none !important;
}


.item-label:focus {
  outline: none;
}

.item-label:focus-visible {
  @apply shadow-[0_0_0_2px_var(--color-primary-300)] dark:shadow-[0_0_0_2px_var(--color-primary-400)];
}


.item-label.selected {
  @apply text-white dark:text-white;
}

.item-label:not(.selected):hover {
  @apply text-gray-900 dark:text-gray-100;
}

.small .item-label {
  padding: 0.25rem 0.5rem;
}

/* Square shape: tight container, square corners, padding following the size */
.magnetic-container.is-square {
  @apply rounded-lg;
  padding: 0.125rem;
  gap: 0.125rem;
}

.is-square .magnetic-background,
.is-square .item-label {
  @apply rounded-md;
}

.is-square .item-label {
  @apply gap-1 font-medium;
}

.is-square.options-dense .item-label {
  padding: 0 0.5rem;
}

.is-square.options-small .item-label {
  padding: 0 0.625rem;
}

.is-square.options-medium .item-label {
  padding: 0 0.75rem;
}

.is-square.options-large .item-label {
  padding: 0 1rem;
}

.medium .item-label {
  padding: 0.375rem 0.75rem;
}
</style>
